// Date rules for the Work tracker. Every date on the page is mm-dd-yyyy, and
// "today" is the viewer's local calendar day (both users are in California).

const pad = (n) => String(n).padStart(2, '0');

// 'yyyy-mm-dd' -> local Date at midnight. null/'' -> null.
export const parseISODate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const toISODate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const startOfToday = (now = new Date()) => new Date(now.getFullYear(), now.getMonth(), now.getDate());

export const formatDate = (dateOrIso) => {
  const date = typeof dateOrIso === 'string' ? parseISODate(dateOrIso.slice(0, 10)) : dateOrIso;
  if (!date) return '';
  return `${pad(date.getMonth() + 1)}-${pad(date.getDate())}-${date.getFullYear()}`;
};

// ISO timestamp -> "09-15-2026 8:20 PM" in local time.
export const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hours = d.getHours() % 12 || 12;
  const ampm = d.getHours() < 12 ? 'AM' : 'PM';
  return `${formatDate(d)} ${hours}:${pad(d.getMinutes())} ${ampm}`;
};

export const addDays = (date, n) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);

// Whole days from today to the due date (negative = overdue). null if undated.
export const daysUntil = (dueIso, today = startOfToday()) => {
  const due = parseISODate(dueIso);
  if (!due) return null;
  return Math.round((due - today) / 86400000);
};

// Signal tone for a due date: 'overdue' (red), 'soon' (orange, within 3
// days including today), or null. Complete tasks never signal.
export const dueTone = (task, today = startOfToday()) => {
  if (task.status === 'complete') return null;
  const days = daysUntil(task.due_on, today);
  if (days === null) return null;
  if (days < 0) return 'overdue';
  if (days <= 3) return 'soon';
  return null;
};

// "Overdue 2 days", "Due today", "Due tomorrow", "Due in 3 days", "Due 11-27-2026".
export const dueLabel = (task, today = startOfToday()) => {
  const days = daysUntil(task.due_on, today);
  if (days === null) return '';
  if (task.status === 'complete') return `Due ${formatDate(task.due_on)}`;
  if (days < 0) return `Overdue ${-days} day${days === -1 ? '' : 's'}`;
  if (days === 0) return 'Due today';
  if (days === 1) return 'Due tomorrow';
  if (days <= 3) return `Due in ${days} days`;
  return `Due ${formatDate(task.due_on)}`;
};

// The colour of a card's left rail / a calendar pill.
export const railTone = (task, today = startOfToday()) => {
  if (task.status === 'complete') return 'complete';
  const due = dueTone(task, today);
  if (due) return due;
  if (task.status === 'needs_review') return 'review';
  return 'neutral';
};

export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// 42 local Dates (6 fixed rows, weeks start Sunday) for the month containing `date`.
export const monthGrid = (date) => {
  const first = new Date(date.getFullYear(), date.getMonth(), 1);
  const start = addDays(first, -first.getDay());
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};
